import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Users, Loader2, DollarSign, TrendingUp, User, Save, Network } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { affApi, clearAffSession, getAffInfo, getAffToken } from "@/lib/affiliateApi";
import { ContactBankFields } from "@/components/admin/ContactBankFields";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("es-CL"); } catch { return iso || "-"; }
};
const fmtCLP = (n) => `$${Number(n || 0).toLocaleString("es-CL")}`;

const STATUS_CLS = {
  nuevo: "bg-blue-100 text-blue-700",
  simulacion_lista: "bg-indigo-100 text-indigo-700",
  simulacion_enviada: "bg-violet-100 text-violet-700",
  en_emision: "bg-amber-100 text-amber-700",
  ganado: "bg-emerald-100 text-emerald-700",
  perdido: "bg-rose-100 text-rose-700",
  contactado: "bg-slate-100 text-slate-600",
  cotizado: "bg-slate-100 text-slate-600",
};

export default function AffiliateDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);
  const [contacto, setContacto] = useState(null);
  const [banco, setBanco] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!getAffToken()) navigate("/afiliado/login");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await affApi.get("/affiliate/dashboard");
      setData(r.data);
      setContacto(r.data.affiliate.contacto || null);
      setBanco(r.data.affiliate.datos_bancarios || null);
    } catch (e) {
      if (e?.response?.status === 401) {
        clearAffSession();
        navigate("/afiliado/login");
      } else toast.error("No se pudo cargar");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await affApi.patch("/affiliate/profile", { contacto, datos_bancarios: banco });
      toast.success("Perfil actualizado");
      setProfileOpen(false);
      load();
    } catch { toast.error("No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const logout = () => { clearAffSession(); navigate("/afiliado/login"); };

  if (loading || !data) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand-gold" /></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/brand/logo.png" alt="OperaSafe.cl" className="h-9 w-auto" />
            <span className="hidden sm:inline text-xs font-semibold uppercase tracking-widest text-gray-400">Portal Afiliado</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-sm text-gray-600">{data.affiliate.name} ({data.affiliate.code})</span>
            <Button variant="outline" size="sm" onClick={() => setProfileOpen(true)} data-testid="aff-profile-btn"><User className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" onClick={logout} data-testid="aff-logout-btn"><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Total leads" value={data.summary.total} accent="bg-blue-100 text-blue-700" />
          <StatCard icon={TrendingUp} label="Emitidos" value={data.summary.ganados} accent="bg-emerald-100 text-emerald-700" />
          <StatCard icon={DollarSign} label="Por cobrar" value={fmtCLP(data.summary.comision_por_cobrar)} accent="bg-amber-100 text-amber-700" />
          <StatCard icon={DollarSign} label="Pagada" value={fmtCLP(data.summary.comision_pagada)} accent="bg-teal-100 text-teal-700" />
        </div>

        {/* Mis subafiliados (si tiene) */}
        {data.sub_affiliates && data.sub_affiliates.length > 0 && (
          <div className="mt-8 bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid="sub-affiliates-section">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
              <Network className="h-4 w-4 text-brand-gold" />
              <h2 className="font-display text-lg font-bold text-brand-ink">Mis subafiliados</h2>
              <span className="text-xs text-gray-500">({data.sub_affiliates.length})</span>
            </div>
            <div className="p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {data.sub_affiliates.map((s) => (
                <div key={s.code} data-testid={`sub-aff-${s.code}`} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="flex items-center gap-2">
                    <span className="font-display text-sm font-bold text-brand-ink">{s.name}</span>
                    <span className="font-mono text-[10px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">{s.code}</span>
                    {s.parent_code && s.parent_code !== data.affiliate.code && (
                      <span className="text-[10px] font-semibold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">via {s.parent_code}</span>
                    )}
                  </div>
                  <div className="mt-3 text-xs text-gray-500">
                    <span className="font-display text-xl font-bold text-brand-ink mr-1">{s.total}</span> leads
                    <span className="ml-3 font-display text-xl font-bold text-brand-ink mr-1">{s.ganados}</span> emitidos
                  </div>
                  <div className="mt-2 text-xs flex flex-wrap gap-x-3 gap-y-1">
                    <span className="text-gray-500">Por cobrar: <span className="font-bold text-emerald-700">{fmtCLP(s.comision_por_cobrar)}</span></span>
                    <span className="text-gray-500">Pagada: <span className="font-bold text-brand-ink">{fmtCLP(s.comision_pagada)}</span></span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200">
            <h2 className="font-display text-lg font-bold text-brand-ink">Tus leads y comisiones</h2>
            <p className="text-xs text-gray-500">
              Comisión aplicada: {data.affiliate.commission_pct != null ? `${data.affiliate.commission_pct}%` : "—"}
              {data.sub_affiliates?.length > 0 && <span className="ml-2 text-brand-gold font-semibold">· Incluye leads de tus subafiliados</span>}
            </p>
          </div>
          {data.leads.length === 0 ? (
            <div className="text-center py-12 text-gray-500">Aún no tienes leads asociados.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Fecha</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Industria</th>
                    {data.sub_affiliates?.length > 0 && <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Afiliado</th>}
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Comisión</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Pagada</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.leads.map((l, i) => (
                    <tr key={i} data-testid={`aff-lead-row-${i}`}>
                      <td className="px-4 py-3 text-xs text-gray-500">{fmtDate(l.fecha)}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-semibold text-brand-ink">{l.industria}</div>
                        {l.equipo && <div className="text-xs text-gray-500">{l.equipo}</div>}
                      </td>
                      {data.sub_affiliates?.length > 0 && (
                        <td className="px-4 py-3 text-xs">
                          {l.owner_code === data.affiliate.code ? (
                            <span className="inline-flex rounded-full bg-blue-100 text-blue-700 px-2 py-0.5 text-[10px] font-semibold">Tú</span>
                          ) : (
                            <span className="inline-flex rounded-full bg-slate-100 text-slate-700 px-2 py-0.5 text-[10px] font-semibold">{l.owner_name || l.owner_code}</span>
                          )}
                        </td>
                      )}
                      <td className="px-4 py-3"><span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_CLS[l.estado] || "bg-gray-100 text-gray-600"}`}>{l.estado_label}</span></td>
                      <td className="px-4 py-3 text-sm font-semibold text-brand-ink">{l.comision ? fmtCLP(l.comision) : "—"}</td>
                      <td className="px-4 py-3 text-xs">{l.comision_pagada === null || l.comision_pagada === undefined ? "—" : (l.comision_pagada ? <span className="text-emerald-700 font-semibold">Sí</span> : <span className="text-amber-700 font-semibold">Pendiente</span>)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {profileOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setProfileOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-200">
              <h3 className="font-display text-xl font-bold text-brand-ink">Mi perfil</h3>
              <p className="text-sm text-gray-500">Contacto y datos bancarios para pagar tus comisiones.</p>
            </div>
            <div className="p-5">
              <ContactBankFields
                contacto={contacto}
                datos_bancarios={banco}
                onChangeContacto={setContacto}
                onChangeBanco={setBanco}
              />
            </div>
            <div className="p-5 border-t border-gray-200 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setProfileOpen(false)}>Cancelar</Button>
              <Button onClick={saveProfile} disabled={saving} className="bg-brand-ink hover:bg-brand-inkHover text-white" data-testid="aff-profile-save">
                {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Guardando</> : <><Save className="h-4 w-4 mr-1" /> Guardar</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-4">
    <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${accent}`}>
      <Icon className="h-4 w-4" />
    </div>
    <p className="mt-3 font-display text-2xl font-bold text-brand-ink">{value}</p>
    <p className="mt-1 text-xs text-gray-500">{label}</p>
  </div>
);
